<template>
    <div style="overflow:hidden;position: relative" :ref="'fileinfo'">
        <div class="flex flex-row" ref="infoflex">
            <div class="p-3 m-0 block flex-grow" ref="imagebox" v-if="prefs.showPreview">
                <img alt="" class="overflow-hidden rounded-lg object-contain shadow-lg" ref="image" :key="thumbnailKey"
                     :src="getThumbnailUrl(file)" @dblclick="preview(file)" @load="resize">
            </div>
            <div class="flex-shrink m-3" style="width: 250px; min-width: 250px; overflow: visible;" ref="infobox"
                 v-if="prefs.showInfo">
                <div class="flex items-start mb-6">
                    <div class="mr-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="19px" height="19px" viewBox="0 0 24 24"
                             fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                             class="text-theme vue-feather feather feather-image">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                    </div>
                    <div>
                        <b class="block overflow-hidden text-ellipsis text-sm">
                            {{ file.name }}
                        </b>
                        <small class="block text-xs text-gray-400">
                            {{ file.mimeType }}
                        </small>
                    </div>
                </div>
                <div class="mb-3">
                    <small class="text-theme block text-xs">
                        Size
                    </small>
                    <b class="inline-block text-sm">
                        {{ bytesToHuman(file.size) }}
                    </b>
                </div>

                <div class="mb-3">
                    <small class="text-theme block text-xs">
                        Created at
                    </small>
                    <b class="inline-block text-sm">
                        {{ file.createdAt.format('MMMM Do YYYY, h:mm:ss a') }}
                    </b>
                </div>
                <div class="mb-3">
                    <small class="text-theme block text-xs">
                        Created by
                    </small>
                    <b class="inline-block text-sm">
                        {{ file.createdBy }}
                    </b>
                </div>
                <div class="mb-3" v-if="file.lastUpdatedAt">
                    <small class="text-theme block text-xs">
                        Last updated at
                    </small>
                    <b class="inline-block text-sm">
                        {{ file.lastUpdatedAt.format('MMMM Do YYYY, h:mm:ss a') }}
                    </b>
                </div>
                <div class="mb-3" v-if="file.lastUpdatedAt">
                    <small class="text-theme block text-xs">
                        Last updated by
                    </small>
                    <b class="inline-block text-sm">
                        {{ file.lastUpdatedBy }}
                    </b>
                </div>
                <div class="mb-3">
                    <small class="text-theme block text-xs">
                        Location
                    </small>
                    <div class="items-center">
                        <template v-for="(loc,index) in this.locations()">
                            <span v-if="loc" class="">&gt;</span>
                            <b v-if="loc && !loc.isRoot"
                               class="cursor-pointer inline-block whitespace-nowrap text-sm hover:text-blue-500 hover:bg-gray-100 rounded border-2 border-none px-1 -ml-1"
                               v-text="loc.name"
                               @click="navigateTo(loc)">
                            </b>
                            <b class="cursor-pointer inline-block whitespace-nowrap text-sm hover:text-blue-500 hover:bg-gray-100 rounded border-2 border-none px-1 -ml-1"
                               v-else @click="navigateTo(null)"><span class='fas fa-home text-sm'
                                                                      style='margin-top:4px;margin-bottom:4px;'></span></b>
                        </template>
                    </div>
                </div>
                <div class="mb-3" v-if="file.tags.length">
                    <small class="text-theme block text-xs">
                        Tags
                    </small>
                    <div v-for="(tag, index) in file.tags" class="flex">
                        <div class="w-4 h-4 rounded-full flex-0 mt-1 mr-1"
                             :style="'background-color: '+(getFsTag(tag)?getFsTag(tag).colorHex:'#FFF')">
                        </div>
                        <span class="flex-1 block overflow-hidden whitespace-nowrap text-s text-ellipsis">
                    {{ tag }}
                </span>
                    </div>
                </div>
                <div class="mb-3" v-if="file.revisions.length">
                    <small class="text-theme block text-xs">
                        Previous Revisions
                    </small>
                    <div v-for="(revision, index) in file.revisions"
                         class="hover:bg-gray-100 rounded-lg border-1 border-none pl-1 pb-0.5 cursor-pointer"
                         @mousedown.prevent
                         @dblclick="(e) => preview(revision)"
                         @contextmenu.prevent.stop="(e) => openContext(revision,e)"
                    >
                <span class="block overflow-hidden whitespace-nowrap text-s p-0 flex-none text-ellipsis font-semibold">
                    {{ revision.name }}
                </span>
                        <span class="block overflow-hidden whitespace-nowrap text-xs p-0 flex-none">
                    Created at - <span
                            class="font-semibold">{{ revision.createdAt.format('D MMM YY - h:mm:ss a') }}</span>
                </span>
                    </div>
                    <vue-context ref="revisionmenu" class="fixed">
                        <div id="revisionmenu"
                             class="p-3 w-64 z-30 bg-white rounded-lg border border-gray-300 shadow-lg">
                            <ui-dropdown-item icon="fa-fw fas fa-download" @click="download(contextRevision)">Download
                                File
                            </ui-dropdown-item>
                            <ui-dropdown-divider></ui-dropdown-divider>
                            <ui-dropdown-item icon="far fa-arrow-alt-circle-up" @click="makeCurrent(contextRevision)">
                                Make
                                this
                                Revision current
                            </ui-dropdown-item>
                        </div>
                    </vue-context>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import UiDropdownItem from "../ui/UiDropdownItem.vue";
import VueContext from "../vue-context/src/js/vue-context";
import UiDropdownDivider from "../ui/UiDropdownDivider.vue";
import {Fs} from "@models";
export default {
    components: {UiDropdownDivider, UiDropdownItem, VueContext},
    props: {
        filesystem: Object,
        file: Object,
        rotations: Object,
        prefs: Object,
    },
    data() {
        return {
            contextRevision: false,
            thumbnailKey:0,
        }
    },
    watch: {
        'rotations.key': {
            handler(a, b) {
                this.$forceUpdate()
            },
        },
    },
    computed: {},
    methods: {
        getFsTag(tag) {
            return this.filesystem.tags.find((t) => t.name === tag)
        },
        openContext(revision, e) {
            this.$emit('closeContextMenus');
            this.contextRevision = revision;
            this.$refs.revisionmenu.open(e)
        },
        download(revision) {
            let a = document.createElement("a")
            document.body.appendChild(a)
            a.style = "display: none"
            a.href =  revision.url + '?forceDownload=1';
            a.download = "download"
            a.click();
            document.body.removeChild(a)
        },
        async makeCurrent(revision) {
            await this.filesystem.promoteRevision(this.file, revision)
        },
        preview(revision) {
            this.$emit('preview', revision)
        },
        bytesToHuman(bytes) {
            if (bytes < 1000) {
                return bytes + ' B'
            } else if (bytes < 1000 * 1000) {
                return Math.round((bytes / 1000) * 100) / 100 + ' kB (' + Math.round((bytes / 1024) * 100) / 100 + ' KiB)'
            } else if (bytes < 1000 * 1000 * 1000) {
                return Math.round((bytes / (1000 * 1000)) * 100) / 100 + ' MB (' + Math.round((bytes / (1024 * 1024)) * 100) / 100 + ' MiB)'
            } else {
                return Math.round((bytes / (1000 * 1000 * 1000)) * 100) / 100 + ' GB (' + Math.round((bytes / (1024 * 1024 * 1024)) * 100) / 100 + ' GiB)'
            }
        },
        locations() {
            let pName = (p) => {
                if (p.parent) {
                    let ret = pName(p.parent)
                    ret.push(p);
                    return ret;
                } else {
                    return [null]
                }
            }
            const parts = pName(this.file);
            parts.pop();
            return parts;
        },
        navigateTo(destination) {
            this.$emit('navigateTo', destination)
        },
        closeMenu() {
            this.$refs.revisionmenu.close()
        },
        getThumbnailUrl() {
            if (!this.file.supportsRotations) {
                return this.file.thumbnail+'&k='+this.thumbnailKey;
            }
            let rot = this.rotations[this.file.id];
            if (!rot) {
                return this.file.thumbnail+'&k='+this.thumbnailKey;
            }
            return (this.file.rotations[rot]?this.file.rotations[rot].thumbnail:this.file.thumbnail)+'&k='+this.thumbnailKey;
        },
        resize() {
            if (this.$refs.fileinfo) {
                if (this.$refs.image) {
                    let maxHeight = window.innerHeight - this.$refs.infoflex.getBoundingClientRect().top - 58
                    this.$refs.image.setAttribute('style', 'max-height:' + maxHeight + 'px')
                }
            }
        },
    },
    updated() {
        this.resize()
    },
    mounted() {
        eventBus.$on('thumbnail-generated-' + this.filesystem.id + '|' + this.file.id, (e) => {
            this.$nextTick(() => {
                this.thumbnailKey = Fs.getThumbnailKey(this.filesystem.id,this.file.id);
            })
        });
        this.thumbnailKey = Fs.getThumbnailKey(this.filesystem.id,this.file.id);
        this.resize()
        window.addEventListener('resize', this.resize, true);
        this.$on('hook:beforeDestroy', () => {
            window.removeEventListener('resize', this.resize)
        })
    }
}
</script>

